<template>
  <div class="clist-root">
    <!-- Filters bar -->
    <section class="clist-filters">
      <div class="search-wrap">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
        </svg>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by client name, vendor, username, email…"
          class="search-input"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="search-clear"
          @click="searchQuery = ''"
          title="Clear search"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <select
        v-if="isSuperAdmin"
        v-model="vendorFilter"
        class="clist-select"
      >
        <option value="">All vendors</option>
        <option v-for="v in vendorOptions" :key="v" :value="v">{{ v }}</option>
      </select>

      <div class="chip-group">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >{{ opt.label }}</button>
      </div>
    </section>

    <!-- Body -->
    <div v-if="isLoading" class="clist-loading">
      <div class="loading-spinner"></div>
      <div class="loading-label">Loading clients…</div>
    </div>

    <div v-else-if="!hasFilteredClients" class="empty-hero">
      <div class="empty-hero-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke-linecap="round" />
        </svg>
      </div>
      <div class="empty-hero-title">No clients found</div>
      <div class="empty-hero-sub">
        {{ searchQuery ? 'Try clearing your search or filters.' : 'Create your first client to get started.' }}
      </div>
      <button v-if="!searchQuery && can('client.create')" class="btn btn-primary btn-inline" @click="$emit('create-clicked')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
          <path d="M12 5v14M5 12h14" stroke-linecap="round" />
        </svg>
        New Client
      </button>
    </div>

    <div v-else class="clist-body">
      <div class="clist-grid">
        <article
          v-for="client in allClients"
          :key="`${client.vendor}-${client.clientName}`"
          class="client-card"
          @click="editClient(client)"
        >
          <div class="card-top">
            <div class="avatar">{{ initialsOf(client) }}</div>
            <div class="client-meta">
              <div class="client-name">{{ client.clientName }}</div>
              <div class="client-sub">
                <span v-if="client.username">@{{ client.username }}</span>
                <span v-if="client.username" class="dot-sep">·</span>
                <span class="vendor-tag">{{ client.vendor }}</span>
              </div>
            </div>
            <div class="card-badges">
              <span
                v-if="Number(client.payout_enabled) === 0"
                class="pill pill-danger"
                title="Payout is disabled for this client — no new payout orders can be created."
              >Payout OFF</span>
              <span
                v-if="client.username"
                class="pill"
                :class="client.status ? 'pill-success' : 'pill-neutral'"
              >{{ client.status ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>

          <div v-if="hasStats(client)" class="card-stats">
            <div class="stat-cell">
              <div class="stat-label">Pay-in today</div>
              <div class="stat-value">{{ formatMoney(getStat(client, 'payInApprovedAmount')) }}</div>
              <div class="stat-sub">{{ getStat(client, 'payInApprovedCount') || 0 }} orders</div>
            </div>
            <div class="stat-cell">
              <div class="stat-label">Pay-out today</div>
              <div class="stat-value">{{ formatMoney(getStat(client, 'payOutApprovedAmount')) }}</div>
              <div class="stat-sub">{{ getStat(client, 'payOutApprovedCount') || 0 }} orders</div>
            </div>
            <div class="stat-cell">
              <div class="stat-label">Commission</div>
              <div class="stat-value stat-mono">
                {{ formatPercent(client.payInCommission) }} <span class="stat-slash">/</span> {{ formatPercent(client.payOutCommission) }}
              </div>
              <div class="stat-sub">pay-in / pay-out</div>
            </div>
          </div>
          <div v-else-if="isLoadingStats" class="card-stats card-stats-loading">
            <div class="skeleton"></div>
            <div class="skeleton"></div>
            <div class="skeleton"></div>
          </div>

          <div class="card-actions" @click.stop>
            <button
              class="action-btn action-report"
              @click.stop="openClientReport(client)"
              title="View client report"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3v18h18" stroke-linecap="round" />
                <path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Report
            </button>
            <div class="action-toggle" v-if="client.username && can('client.edit')">
              <span class="toggle-label">Status</span>
              <base-toggle
                :key="`toggle-${client.username}-${toggleVersions[client.username] || 0}`"
                class="mb-0"
                :is-checked="client.status === 1"
                @rsd-checkbox-changed="toggleStatus(client)"
              ></base-toggle>
            </div>
            <button
              v-if="can('client.edit')"
              class="action-btn action-edit"
              @click.stop="editClient(client)"
              title="Edit client"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9" stroke-linecap="round" />
                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Edit
            </button>
          </div>
        </article>
      </div>

      <div class="pagination-wrap">
        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
      </div>
    </div>

    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="openClientStatistics" class="modal-backdrop" @click.self="closeClientReport">
          <div class="modal-card modal-card-wide">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Client report</div>
                <div class="modal-title">{{ selectedClient.clientName }}</div>
                <div class="modal-sub">Pay-in and pay-out activity across all statuses.</div>
              </div>
              <button type="button" class="modal-close" @click="closeClientReport">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="modal-body modal-body-scroll">
              <client-report
                :client-name="selectedClient.clientName"
                :vendor="selectedClient.vendor"
              ></client-report>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import ClientReport from "../../components/clients/ClientReport.vue";
import Pagination from "../../components/shared/pagination.vue";
import http from "../../http-common";
import moment from "moment-timezone";

export default {
  name: "clients-list",
  emits: ["edit-client", "create-clicked"],
  components: { ClientReport, Pagination },
  data() {
    return {
      isLoading: false,
      isLoadingStats: false,
      statsDate: moment().tz(process.env.VUE_APP_TIMEZONE || "Asia/Kolkata").format("YYYY-MM-DD"),
      clientStats: {},
      page: 1,
      perPage: 12,
      openClientStatistics: false,
      selectedClient: { clientName: "", vendor: "" },
      searchQuery: "",
      vendorFilter: "",
      statusFilter: "active",
      statusOptions: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
      vendorOptions: [],
      // Per-client toggle re-render counters. Bumped on dialog cancel/error
      // so the <base-toggle> instance is recreated and the DOM re-syncs.
      toggleVersions: {},
    };
  },
  created() {
    this.getClients();
    if (this.isSuperAdmin) this.loadVendorOptions();
  },
  watch: {
    page() { this.getClients(); },
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        if (this.page !== 1) this.page = 1;
        else this.getClients();
      }, 300);
    },
    statusFilter() {
      if (this.page !== 1) this.page = 1;
      else this.getClients();
    },
    vendorFilter() {
      if (this.page !== 1) this.page = 1;
      else this.getClients();
    },
  },
  computed: {
    hasClients() { return this.$store.getters["clients/hasClients"]; },
    allClients() { return this.$store.getters["clients/clients"]; },
    hasFilteredClients() { return (this.allClients || []).length > 0; },
    paginatorInfo() { return this.$store.getters["clients/pagination"]; },
    isSuperAdmin() { return !!this.$store.getters.isSuperAdmin; },
  },
  methods: {
    can(perm) { return this.$store.getters.hasPermission(perm); },
    setPage(page) { this.page = page; },
    initialsOf(client) {
      const s = (client.clientName || client.username || "?").trim();
      const parts = s.split(/\s+/);
      const a = parts[0]?.[0] || "?";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase().slice(0, 2);
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatPercent(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      const trimmed = n % 1 === 0 ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
      return trimmed + "%";
    },
    hasStats(client) {
      const s = this.clientStats[client.clientName];
      if (!s) return false;
      return (
        Number(s.payInApprovedCount) > 0 ||
        Number(s.payOutApprovedCount) > 0 ||
        this.hasCommission(client)
      );
    },
    hasCommission(client) {
      return Number(client.payInCommission) > 0 || Number(client.payOutCommission) > 0;
    },
    getStat(client, key) {
      const s = this.clientStats[client.clientName];
      return s ? s[key] : 0;
    },
    async getClients() {
      this.isLoading = true;
      try {
        await this.$store.dispatch("clients/getClients", {
          first: this.perPage,
          page: this.page,
          status: this.statusFilter,
          search: this.searchQuery && this.searchQuery.trim(),
          vendor: this.vendorFilter,
        });
        await this.loadStats();
      } catch (error) {
        this.error =
          (error && error.message && error.message.message) ||
          (error && error.message) ||
          "Something failed!";
      }
      this.isLoading = false;
    },
    async loadVendorOptions() {
      try {
        const response = await http.get("/vendor");
        const list = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || [];
        this.vendorOptions = list
          .map((v) => v && v.username)
          .filter(Boolean)
          .sort();
      } catch (e) {
        this.vendorOptions = [];
      }
    },
    async loadStats() {
      this.isLoadingStats = true;
      try {
        const response = await http.get(
          `/dashboard/list-stats?type=client&date=${this.statsDate}`
        );
        if (response.data.success) {
          const map = {};
          response.data.data.forEach((item) => {
            map[item.clientName] = item;
          });
          this.clientStats = map;
        }
      } catch (e) {
        console.error("Error loading client stats:", e);
      }
      this.isLoadingStats = false;
    },
    editClient(client) { if (!this.can("client.edit")) return; this.$emit("edit-client", client); },
    openClientReport(client) {
      this.selectedClient = { clientName: client.clientName, vendor: client.vendor };
      this.openClientStatistics = true;
    },
    closeClientReport() {
      this.openClientStatistics = false;
      this.selectedClient = { clientName: "", vendor: "" };
    },
    async toggleStatus(client) {
      if (!client.username) return;
      const newStatus = client.status === 1 ? 0 : 1;
      const action = newStatus === 0 ? "deactivate" : "activate";
      const title = newStatus === 0
        ? `Deactivate client "${client.clientName}"?`
        : `Activate client "${client.clientName}"?`;
      const html = newStatus === 0
        ? `<div class="text-left"><p class="mb-2"><strong>This is a high-impact action.</strong></p><p>All <strong>new orders</strong> for this client will be <strong>stopped immediately</strong>.</p><p class="mt-2 text-sm text-gray-600">Existing orders are not changed.</p></div>`
        : `<div class="text-left"><p>This client will be allowed to create new orders again.</p></div>`;
      this.$swal.fire({
        title,
        html,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: newStatus === 0 ? "#d33" : "#3085d6",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `Yes, ${action} client`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$store.dispatch("clients/changeClientStatus", {
              username: client.username,
              status: newStatus,
            });
          } catch (error) {
            this.error = error.message || "Something failed!";
            this.bumpToggleVersion(client.username);
          }
        } else {
          this.bumpToggleVersion(client.username);
        }
      });
    },
    bumpToggleVersion(username) {
      this.toggleVersions = {
        ...this.toggleVersions,
        [username]: (this.toggleVersions[username] || 0) + 1,
      };
    },
  },
};
</script>

<style scoped>
.clist-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

/* ------------- Filters bar ------------- */
.clist-filters {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.search-input:focus { border-color: #1B2A47; background: #fff; }
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.clist-select {
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 12px;
  background: #f8fafc;
  outline: none;
  color: #475569;
  cursor: pointer;
}
.clist-select:focus { border-color: #1B2A47; background: #fff; }

.chip-group { display: flex; gap: 4px; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
}
.chip:hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

/* ------------- Body / loading / empty ------------- */
.clist-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.clist-loading {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.empty-hero-icon {
  width: 60px; height: 60px;
  border-radius: 16px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 30px; height: 30px; }
.empty-hero-title { font-size: 16px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 13px; color: #64748b; }
.btn-inline { margin-top: 8px; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }

/* ------------- Client card ------------- */
.clist-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 1200px) {
  .clist-grid { grid-template-columns: 1fr 1fr; }
}

.client-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.client-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 8px 22px -10px rgba(27, 42, 71, 0.22);
  transform: translateY(-1px);
}

.card-top {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
}
.avatar {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.client-meta { min-width: 0; }
.client-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.client-sub {
  font-size: 12px;
  color: #64748b;
  display: flex;
  gap: 6px;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dot-sep { color: #cbd5e1; }
.vendor-tag {
  color: #475569;
  font-weight: 500;
}

.card-badges {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.pill-success { background: #d1fae5; color: #065f46; }
.pill-danger  { background: #fee2e2; color: #991b1b; }
.pill-neutral { background: #f1f5f9; color: #64748b; }

/* ------------- Stats ------------- */
.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}
.card-stats-loading .skeleton {
  height: 46px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 400% 100%;
  border-radius: 8px;
  animation: shine 1.4s ease-in-out infinite;
}
@keyframes shine {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.stat-cell {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 8px 10px;
}
.stat-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}
.stat-mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 13px; }
.stat-slash { color: #cbd5e1; margin: 0 2px; }
.stat-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}
@media (max-width: 720px) {
  .card-stats { grid-template-columns: 1fr 1fr; }
  .card-stats > .stat-cell:nth-child(3) { grid-column: 1 / -1; }
}

/* ------------- Card actions ------------- */
.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
}
.action-btn svg { width: 14px; height: 14px; }
.action-btn:hover { border-color: #cbd5e1; color: #1B2A47; background: #eef2f7; }
.action-report:hover { color: #059669; background: #ecfdf5; border-color: #a7f3d0; }
.action-edit:hover  { color: #1B2A47; background: #eef2f7; border-color: #cbd5e1; }

.action-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}
.toggle-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* ------------- Pagination ------------- */
.pagination-wrap {
  margin-top: auto;
  padding-top: 8px;
}

/* Fade transition (reused by modal) */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ==================== Report modal (teleported) ==================== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.modal-card-wide { max-width: 840px; }
.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.modal-sub { font-size: 12.5px; color: #64748b; }
.modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.modal-close svg { width: 16px; height: 16px; }

.modal-body {
  padding: 20px 22px;
}
.modal-body-scroll { overflow-y: auto; max-height: calc(100vh - 200px); }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .modal-card { transform: translateY(-8px) scale(0.98); }
</style>
